// ServiceProductsPage.tsx
// Página para contratar productos de un servicio concreto.
// Carga los productos disponibles para el servicio y permite contratarlos.

import React, { useEffect, useRef, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api, { restoreAuthFromStorage } from '../api/Client';
import { Browser } from '@capacitor/browser';
import { Camera, CameraResultType, CameraSource } from '@capacitor/camera';
import { Capacitor } from '@capacitor/core';
import PaymentWebView from '../components/PaymentWebView';
import { SecureStorage } from '../storage/SecureStorage';
import {API_BASE} from "../config.ts";
import { fetchUserProfile } from '../api/User';

interface ServiceProduct {
  id: number;
  name: string;
  description?: string | null;
  session?: number | null;
  price?: number | null;
  discount?: number | null;
  image?: string | null;
  type_of_product?: string | null; // "recurrent", "multi_sessions", etc.
  centro?: string | null;
}

interface Coupon {
  id: number;
  code?: string;
  coupon_code?: string;
  discount?: number;
  isPercentage?: boolean;
  expiryDate?: string; // ISO date
  productIds?: number[];
}

function isCouponApplicableToProduct(coupon: Coupon, productId: number): boolean {
  const productList = Array.isArray(coupon.productIds) ? coupon.productIds : [];
  if (!productList.includes(productId)) return false;

  if (coupon.expiryDate) {
    const expiryDate = new Date(coupon.expiryDate);
    if (!Number.isNaN(expiryDate.getTime()) && expiryDate.getTime() < Date.now()) {
      return false;
    }
  }

  return true;
}

function extractCouponCode(coupon: Coupon): string | null {
  const rawCode = coupon.code ?? coupon.coupon_code;
  if (typeof rawCode !== 'string') return null;
  const trimmed = rawCode.trim();
  return trimmed.length > 0 ? trimmed : null;
}

/** === Helpers de clasificación === */
function isSubscription(prod: ServiceProduct): boolean {
  return prod.type_of_product === 'recurrent';
}

/** Traducción de tipos para UI: Sesión | Bono | Suscripción */
type LogicalKind = 'Sesión' | 'Bono' | 'Suscripción';
function productKind(prod: ServiceProduct): LogicalKind {
  if (isSubscription(prod)) return 'Suscripción';
  const sessions = Number(prod.session ?? 0);
  if (sessions > 1) return 'Bono';
  return 'Sesión';
}

// ID del producto "Aprende a entrenar" (recurrente)
const APRENDE_PRODUCT_ID = 61;

export default function ServiceProductsPage() {
  const { serviceId } = useParams<{ serviceId: string }>();
  const serviceIdNumber = parseInt(serviceId || '0', 10);
  const navigate = useNavigate();

  const [products, setProducts] = useState<ServiceProduct[]>([]);
  const [userProducts, setUserProducts] = useState<number[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [coupons, setCoupons] = useState<Coupon[]>([]);
  const [paymentHtml, setPaymentHtml] = useState<string | null>(null);
  const [pendingMethod, setPendingMethod] = useState<string | null>(null);

  const [showCenterModal, setShowCenterModal] = useState(false);
  const [selectedCenter, setSelectedCenter] = useState<string | null>(null);

  const [isTrainingService, setIsTrainingService] = useState<boolean>(false);
  const [hasTrainingProduct, setHasTrainingProduct] = useState<boolean>(false);

  // 🔴 Booleano que viene del backend (users.aprende_entrenar)
  const [learnsToTrain, setLearnsToTrain] = useState<boolean>(false);

  // Filtros (ya traducidos)
  const [selectedType, setSelectedType] = useState<'Todos' | LogicalKind>('Todos');
  const [selectedSession, setSelectedSession] = useState<string>('Todas');

  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState<ServiceProduct | null>(null);

  const [showUserInfoModal, setShowUserInfoModal] = useState(false);
  const [missingDni, setMissingDni] = useState(false);
  const [missingAddress, setMissingAddress] = useState(false);
  const [missingProfilePic, setMissingProfilePic] = useState(false);
  const [tempDni, setTempDni] = useState('');
  const [tempAddress, setTempAddress] = useState('');
  const [tempProfilePicFile, setTempProfilePicFile] = useState<File | null>(null);
  const [tempProfilePicPreview, setTempProfilePicPreview] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement | null>(null);


  // Actualiza el flag aprende_entrenar en BD tras completar el pago del producto "Aprende a entrenar"
  async function updateLearnsToTrainIfNeeded(purchasedProductId: number) {
    try {
      if (purchasedProductId !== APRENDE_PRODUCT_ID || learnsToTrain) return;
      const { user } = await restoreAuthFromStorage();
      if (!user?.id) return;
      await api.post(`/api/customers/learn/update/${user.id}`, {
        aprende_entrenar: true,
      });
      setLearnsToTrain(true);
    } catch (err) {
      console.warn('No se pudo actualizar aprende_entrenar tras el pago:', err);
    }
  }

  useEffect(() => {
    async function fetchData() {
      setLoading(true);
      setError(null);
      try {
        const { user } = await restoreAuthFromStorage();
        if (!user?.id) throw new Error('Usuario no disponible.');

        // Productos del servicio
        const respProducts = await api.get('/api/mobile/service-products', {
          params: { primary_service_id: serviceIdNumber },
        });
        setProducts(Array.isArray(respProducts.data) ? respProducts.data : []);

        // Productos del usuario
        const respUserProd = await api.get('/api/mobile/user-products', {
          params: { user_id: user.id },
        });
        const userProdData: any[] = Array.isArray(respUserProd.data) ? respUserProd.data : [];
        const ids = userProdData.map((p: any) => p.id);
        setUserProducts(ids);

        // ¿Servicio ENTRENAMIENTO?
        try {
          const respServices = await api.get('/api/mobile/services');
          const servicesData: any[] = Array.isArray(respServices.data) ? respServices.data : [];
          const trainingService = servicesData.find((s: any) =>
            String(s.name || '').toUpperCase().includes('ENTRENAMIENTO')
          );
          const trainingId = trainingService?.id;
          setIsTrainingService(serviceIdNumber === trainingId);

          if (trainingId) {
            const hasTraining = userProdData.some((prod: any) => {
              const serviceIds: number[] = Array.isArray(prod.service_ids) ? prod.service_ids : [];
              return serviceIds.includes(trainingId);
            });
            setHasTrainingProduct(hasTraining);
          } else {
            setHasTrainingProduct(false);
          }
        } catch (servErr) {
          console.warn('Error al comprobar servicios', servErr);
          setIsTrainingService(false);
          setHasTrainingProduct(false);
        }

        // Booleano “Aprende a entrenar”
        try {
          const respLearn = await api.get<{ aprende_entrenar?: boolean }>(`/api/customers/learn/status/${user.id}`);
          const flag = !!(respLearn?.data?.aprende_entrenar);
          setLearnsToTrain(flag);
        } catch (learnErr) {
          console.warn("No se pudo obtener 'Aprende a entrenar'", learnErr);
          setLearnsToTrain(false);
        }

        // Cupones
        try {
          const respCoupons = await api.get(`/api/mobile/user/${user.id}/coupon`, {
            params: { user_id: user.id },
          });
          setCoupons(Array.isArray(respCoupons.data) ? respCoupons.data : []);
        } catch (couponErr) {
          console.warn('Error al cargar cupones', couponErr);
          setCoupons([]);
        }
      } catch (err: any) {
        setError(err?.response?.data?.error || err.message || 'Error al cargar productos');
      } finally {
        setLoading(false);
      }
    }
    if (serviceIdNumber > 0) {
      fetchData();
    }
  }, [serviceIdNumber]);

  const findActiveCouponForProduct = React.useCallback(
    (productId: number): Coupon | null => {
      for (const coupon of coupons) {
        if (isCouponApplicableToProduct(coupon, productId)) {
          return coupon;
        }
      }
      return null;
    },
    [coupons]
  );

  const resolveCouponCode = React.useCallback(
    (productId: number): string | null => {
      const coupon = findActiveCouponForProduct(productId);
      return coupon ? extractCouponCode(coupon) : null;
    },
    [findActiveCouponForProduct]
  );

  // Contrata un producto para el usuario actual
  async function hireProductAfterPayment(productId: number, paymentMethod: string) {
    try {
      const { user } = await restoreAuthFromStorage();
      if (!user?.id) throw new Error('Usuario no disponible.');
      const couponCode = resolveCouponCode(productId);
      const resp = await api.post('/api/mobile/assign-product', {
        user_id: user.id,
        product_id: productId,
        payment_method: paymentMethod,
        coupon_code: couponCode ?? null,
        centro: selectedCenter,
      });
      // Refrescar la lista de productos contratados
      const respUserProd = await api.get('/api/mobile/user-products', {
        params: { user_id: user.id },
      });
      const ids = Array.isArray(respUserProd.data)
        ? respUserProd.data.map((p: any) => p.id)
        : [];
      setUserProducts(ids);
      await updateLearnsToTrainIfNeeded(productId);
      alert('Producto contratado correctamente');
      navigate(`/product/${productId}`);
    } catch (err: any) {
      alert(err?.response?.data?.error || err.message || 'Error al contratar producto');
    }
  }

  async function payWithCardAfter15th(_product: ServiceProduct) {
    const message = 'Los pagos con tarjeta realizados a partir del día 15 se procesarán a primeros del mes siguiente. ¿Deseas continuar?';
    const userAccepted = window.confirm(message);
    if (!userAccepted) {
      alert('Pago cancelado');
      return;
    }
    await hireProductAfterPayment(_product.id, 'card');
  }

  // Métodos de pago
  async function payWithGPay(_product: ServiceProduct): Promise<boolean> {
    /*try {
      const { user } = await restoreAuthFromStorage();
      if (!user?.id) throw new Error('Usuario no disponible.');
      const discounted = getDiscountedPrice(_product);
      const amountCents = Math.round(discounted * 100);
      const paymentToken = 'dummy-token';
      const subscription = isSubscription(_product);
      const body: Record<string, any> = {
        token: paymentToken,
        amount: amountCents,
        currency: 'EUR',
        payer_ref: `user_${user.id}`,
        subscribe: subscription ? 'true' : 'false',
        product_id: _product.id,
        user_id: user.id,
      };
      if (subscription) body.interval_months = 1;
      await api.post('/api/payments/googlepay', body);
      return true;
    } catch (err) {
      console.error('Error en payWithGPay', err);
      return false;
    }*/
    alert("🚧 Google Pay todavía no está implementado.");
    return false;
  }

  async function checkUserHasStoredCard(userId: number): Promise<boolean> {
    try {
      const response = await api.get<{ hasData?: boolean }>(
        `/api/customers/cards/has-data/${userId}`
      );
      return Boolean(response?.data?.hasData);
    } catch (err) {
      console.warn('Error comprobando tarjeta guardada', err);
      throw new Error('No se pudo verificar si tienes una tarjeta guardada. Inténtalo de nuevo en unos minutos.');
    }
  }

  async function payWithCard(_product: ServiceProduct): Promise<string | null> {
    try {
      const user = await SecureStorage.getUser();
      if (!user?.id) throw new Error('Usuario no disponible.');

      const discounted = getDiscountedPrice(_product);
      const amountMinorUnits = Math.round((discounted ?? 0) * 100);

      const nameParts = (user?.fullName ?? 'USUARIO').trim().split(' ');
      const firstName = nameParts[0] || 'USUARIO';
      const lastName = nameParts.slice(1).join(' ') || 'APELLIDO';
      const email = user?.email || 'usuario@example.com';
      const street = user.postAddress || 'Calle Falsa 123';
      const city = user.city || 'Segovia';
      const postal = user.postCode || '40000';

      const subscription = isSubscription(_product);

      const params: Record<string, any> = {
        amount: amountMinorUnits,
        currency: 'EUR',
        firstName,
        lastName,
        email,
        street,
        city,
        postalCode: postal,
        payer_ref: `user_${user.id}`,
        select_stored_cards: true,
        subscribe: subscription,
        product_id: _product.id,
        user_id: user.id,
      };
      if (subscription) params.interval_months = 1;

      const resp = await api.get('/api/payments/hpp-url', { params });
      if (typeof resp.data === 'string') {
        return resp.data;
      }
      return null;
    } catch (err) {
      console.error('Error en payWithCard', err);
      return null;
    }
  }

  async function payWithEWallet(_product: ServiceProduct): Promise<boolean> {
    return true;
  }

  function clearPendingProfilePic() {
    setTempProfilePicFile(null);
    setTempProfilePicPreview(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  }

  async function pickProfilePic() {
    if (Capacitor.getPlatform() === 'web') {
      fileInputRef.current?.click();
      return;
    }

    try {
      const image = await Camera.getPhoto({
        quality: 80,
        allowEditing: true,
        resultType: CameraResultType.DataUrl,
        source: CameraSource.Prompt,
      });

      if (!image.dataUrl) return;

      const response = await fetch(image.dataUrl);
      const blob = await response.blob();
      setTempProfilePicFile(
        new File([blob], `profile_${Date.now()}.jpg`, { type: 'image/jpeg' })
      );
      setTempProfilePicPreview(image.dataUrl);
    } catch (err) {
      console.warn('Selección de foto cancelada o fallida', err);
    }
  }

  function handleProfilePicChange(event: React.ChangeEvent<HTMLInputElement>) {
    const file = event.target.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = () => {
      setTempProfilePicFile(file);
      setTempProfilePicPreview(typeof reader.result === 'string' ? reader.result : null);
    };
    reader.readAsDataURL(file);
  }

  async function handlePayment(product: ServiceProduct, method: string) {
    try {
      const user = await SecureStorage.getUser();
      if (!user?.id) throw new Error('Usuario no disponible.');
      const hasMissingDni = !String(user?.dni || '').trim();
      const postAddress = String(user?.postAddress ?? user?.address ?? '').trim();
      const hasMissingAddress = !postAddress;
      const hasProfilePic = Boolean(user?.profilePictureName ?? (user as any)?.profile_pic);

      if (hasMissingDni || hasMissingAddress) {
        setMissingDni(hasMissingDni);
        setMissingAddress(hasMissingAddress);
        setMissingProfilePic(!hasProfilePic);
        setTempDni(String(user?.dni || ''));
        setTempAddress(postAddress);
        clearPendingProfilePic();
        setPendingMethod(method);
        setShowUserInfoModal(true);
        return;
      }

      if (method === 'card' && isSubscription(product)) {
        try {
          const hasStoredCard = await checkUserHasStoredCard(user.id);
          if (!hasStoredCard) {
            alert('Necesitas registrar una tarjeta antes de contratar un producto recurrente.');
            setShowPaymentModal(false);
            setSelectedProduct(null);
            return;
          }
        } catch (cardCheckErr: any) {
          alert(cardCheckErr?.message || 'No se pudo verificar si tienes una tarjeta guardada.');
          setShowPaymentModal(false);
          setSelectedProduct(null);
          return;
        }
      }

      const finalPrice = getDiscountedPrice(product);
      if (finalPrice <= 0) {
        alert('Este producto es gratuito, no se realizará ningún cobro. Se asignará automáticamente mediante monedero virtual.');
        await hireProductAfterPayment(product.id, 'ewallet');
        setSelectedProduct(null);
        return;
      }
      const today = new Date();
      if (method === 'card' && isSubscription(product) && today.getDate() > 15) {
        await payWithCardAfter15th(product);
        return;
      } else if (method === 'card') {
        const html = await payWithCard(product);
        if (html) {
          setPaymentHtml(html);
          return;
        }
      } else if (method === 'gpay') {
        const ok = await payWithGPay(product);
        if (ok) await hireProductAfterPayment(product.id, method);
      } else if (method === 'ewallet' || method === 'cash') {
        const ok = await payWithEWallet(product);
        if (ok) await hireProductAfterPayment(product.id, method);
      }
    } catch (e: any) {
      alert(e?.message || 'Error al procesar el pago');
    } finally {
      setShowPaymentModal(false);
    }
  }

  async function openTrainingInfo() {
    try {
      await Browser.open({ url: 'https://www.instagram.com/p/DF2UT5DNnqf/' });
    } catch (err) {
      console.error('No se pudo abrir el enlace de Instagram', err);
    }
  }

  function getDiscountedPrice(prod: ServiceProduct): number {
    let basePrice = prod.price ?? 0;
    coupons.forEach((coupon) => {
      if (!isCouponApplicableToProduct(coupon, prod.id)) {
        return;
      }

      const discountValue = Number(coupon.discount ?? 0);
      if (Number.isNaN(discountValue) || discountValue === 0) {
        return;
      }

      if (coupon.isPercentage) {
        basePrice -= basePrice * (discountValue / 100);
      } else {
        basePrice -= discountValue;
      }
    });
    return basePrice < 0 ? 0 : Number(basePrice.toFixed(2));
  }

  /** === Opciones de filtros (traducidas y robustas) === */
  const typeOptions = React.useMemo(() => {
    const kinds = new Set<LogicalKind>();
    products.forEach(p => kinds.add(productKind(p)));
    // Solo mostrar los tipos existentes en los productos
    const ordered: LogicalKind[] = ['Sesión', 'Bono', 'Suscripción'];
    const present = ordered.filter(k => kinds.has(k));
    return ['Todos', ...present] as const;
  }, [products]);

  const sessionOptions = React.useMemo(() => {
    const all = products
      .map((p) => (p.session == null ? null : Number(p.session)))
      .filter((n): n is number => Number.isFinite(n));
    const uniqueSorted = Array.from(new Set(all)).sort((a, b) => a - b);
    return ['Todas', ...uniqueSorted.map(n => String(n))];
  }, [products]);

  async function handleSaveUserInfo() {
    try {
      const user = await SecureStorage.getUser();
      if (!user?.id) return alert('Usuario no disponible.');

      if (missingDni &&  !/^(\d{8}[A-Z]|X\d{7}[A-Z])$/.test(tempDni.trim())) {
        return alert("DNI inválido: debe tener 8 números y una letra mayúscula o X + 7 números + letra mayúscula");
      }

      if (missingAddress && !tempAddress.trim()) {
        return alert('La dirección postal es obligatoria para continuar con el pago');
      }

      if (missingProfilePic && !tempProfilePicFile) {
        return alert('La foto de perfil es obligatoria para continuar con la contratación');
      }

      const formData = new FormData();
      formData.append('user_id', String(user.id));

      if (missingDni) {
        formData.append('dni', tempDni.trim());
      }

      if (missingAddress) {
        formData.append('direccionPostal', tempAddress.trim());
      }

      if (missingProfilePic) {
        formData.append('require_profile_pic', '1');
      }

      if (tempProfilePicFile) {
        formData.append('profile_pic', tempProfilePicFile);
      }

      const resp = await api.put('/api/mobile/update-pay-info', formData);

      if (resp.status === 200) {
        const updatedUser = await fetchUserProfile(user.id);
        alert('Datos actualizados correctamente ✅');
        clearPendingProfilePic();
        setMissingDni(false);
        setMissingAddress(false);
        setMissingProfilePic(false);
        setShowUserInfoModal(false);

        if (selectedProduct && pendingMethod) {
          const nextMethod = pendingMethod;
          setPendingMethod(null);
          setTempDni(updatedUser?.dni || '');
          setTempAddress(updatedUser?.postAddress || '');
          setTimeout(() => handlePayment(selectedProduct, nextMethod), 500);
        }
      }
    } catch (err: any) {
      console.error('Error al actualizar datos:', err);
      alert(err?.response?.data?.error || 'Error al guardar los datos');
    }
  }


  /** === Aplicación de filtros === */
  const filteredProducts = products
    .filter((p) => {
      const kind = productKind(p);
      const matchType = selectedType === 'Todos' || kind === selectedType;
      const matchSession = selectedSession === 'Todas' || String(p.session ?? '') === selectedSession;
      return matchType && matchSession;
    })
    // Ocultar el producto "Aprende a entrenar" si ya está completado
    .filter((p) => !(learnsToTrain && p.id === APRENDE_PRODUCT_ID));


  return (
    <div style={{ minHeight: "100vh", padding: "1rem", paddingBottom: "80px" }}>
      {loading && <p>Cargando…</p>}
      {error && <p style={{ color: "red" }}>{error}</p>}

      {!loading && products.length > 0 && (
        <div style={{ display: "flex", gap: "0.75rem", marginBottom: "1rem" }}>
          <div style={{ flex: 1 }}>
            <select
              value={selectedType}
              onChange={(e) => setSelectedType(e.target.value as 'Todos' | LogicalKind)}
              style={{
                width: "100%",
                padding: "0.5rem",
                borderRadius: "8px",
                border: "1px solid #ccc",
              }}
            >
              {typeOptions.map((opt) => (
                <option key={opt} value={opt}>
                  {opt}
                </option>
              ))}
            </select>
          </div>
          <div style={{ flex: 1 }}>
            <select
              value={selectedSession}
              onChange={(e) => setSelectedSession(e.target.value)}
              style={{
                width: "100%",
                padding: "0.5rem",
                borderRadius: "8px",
                border: "1px solid #ccc",
              }}
            >
              {sessionOptions.map((opt) => (
                <option key={opt} value={opt}>
                  {opt === "Todas" ? "Todas las sesiones" : `${opt} sesiones`}
                </option>
              ))}
            </select>
          </div>
        </div>
      )}

      {showUserInfoModal && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            backgroundColor: 'rgba(0,0,0,0.4)',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            alignItems: 'center',
            zIndex: 2000,
          }}
        >
          <div
            style={{
              background: '#fff',
              borderRadius: '12px',
              padding: '1.5rem',
              width: '90%',
              maxWidth: 400,
            }}
          >
            <h3 style={{ marginTop: 0 }}>Completa tus datos antes de pagar</h3>
            {missingDni && (
              <>
                <label style={{ fontSize: 14 }}>DNI *</label>
                <input
                  type="text"
                  placeholder="Introduce tu DNI"
                  value={tempDni}
                  onChange={(e) => setTempDni(e.target.value)}
                  style={{
                    width: '100%',
                    padding: '0.5rem',
                    marginBottom: '0.75rem',
                    border: '1px solid #ccc',
                    borderRadius: '6px',
                  }}
                />
              </>
            )}
            {missingAddress && (
              <>
                <label style={{ fontSize: 14 }}>Dirección postal *</label>
                <input
                  type="text"
                  placeholder="Ej: Calle Real 123, Segovia"
                  value={tempAddress}
                  onChange={(e) => setTempAddress(e.target.value)}
                  style={{
                    width: '100%',
                    padding: '0.5rem',
                    marginBottom: '0.75rem',
                    border: '1px solid #ccc',
                    borderRadius: '6px',
                  }}
                />
              </>
            )}
            {missingProfilePic && (
              <>
                <label style={{ fontSize: 14, display: 'block', marginBottom: '0.5rem' }}>
                  Foto de perfil *
                </label>
                <div
                  onClick={pickProfilePic}
                  style={{
                    width: 96,
                    height: 96,
                    borderRadius: '50%',
                    overflow: 'hidden',
                    border: '2px solid #ddd',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    background: '#f9f9f9',
                    cursor: 'pointer',
                    margin: '0 auto 0.75rem',
                  }}
                >
                  {tempProfilePicPreview ? (
                    <img
                      src={tempProfilePicPreview}
                      alt="Vista previa de la foto de perfil"
                      style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                    />
                  ) : (
                    <span style={{ fontSize: 24 }}>+</span>
                  )}
                </div>
                <button
                  type="button"
                  onClick={pickProfilePic}
                  style={{
                    width: '100%',
                    padding: '0.65rem',
                    background: '#f3f7ff',
                    color: '#1976d2',
                    border: '1px solid #c8daf7',
                    borderRadius: '8px',
                    marginBottom: '0.35rem',
                  }}
                >
                  {tempProfilePicFile ? 'Cambiar foto seleccionada' : 'Añadir foto obligatoria'}
                </button>
                <p style={{ marginTop: 0, marginBottom: '0.75rem', fontSize: 12, color: '#666' }}>
                  Debes subir una foto de perfil para continuar con la contratación.
                </p>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/jpeg,image/png"
                  style={{ display: 'none' }}
                  onChange={handleProfilePicChange}
                />
              </>
            )}

            <button
              onClick={handleSaveUserInfo}
              style={{
                width: '100%',
                padding: '0.75rem',
                background: '#1976d2',
                color: '#fff',
                border: 'none',
                borderRadius: '8px',
                marginTop: '0.5rem',
              }}
            >
              Guardar y continuar
            </button>

            <button
              onClick={() => {
                alert('Debes completar tus datos antes de continuar con el pago.');
                clearPendingProfilePic();
                setShowUserInfoModal(false);
                setMissingDni(false);
                setMissingAddress(false);
                setMissingProfilePic(false);
                setPendingMethod(null);
                setSelectedProduct(null);
              }}
              style={{
                width: '100%',
                padding: '0.75rem',
                background: 'transparent',
                color: '#1976d2',
                border: 'none',
                marginTop: '0.5rem',
              }}
            >
              Cancelar
            </button>
          </div>
        </div>
    )}

      {!loading && isTrainingService && !learnsToTrain && (
        <div
          style={{
            backgroundColor: "#fff7e6",
            border: "1px solid #fdd835",
            padding: "1rem",
            borderRadius: "12px",
            marginBottom: "1rem",
            color: "#333",
            fontSize: "0.85rem",
          }}
        >
          <p style={{ margin: 0 }}>
            Antes de contratar otros productos de entrenamiento, debes completar el
            producto <strong>"Aprende a entrenar"</strong>.
          </p>
          <p style={{ margin: 0 }}>
            Para saber por qué, pulsa{' '}
            <span
              onClick={openTrainingInfo}
              style={{
                color: "#1976d2",
                textDecoration: "underline",
                cursor: "pointer",
              }}
            >
              aquí
            </span>
            .
          </p>
        </div>
      )}

      {!loading &&
        filteredProducts.map((prod) => {
          const discounted = getDiscountedPrice(prod);
          const hasDiscount = prod.price != null && discounted < (prod.price ?? 0);
          const isHired = userProducts.includes(prod.id);
          const nameUpper = String(prod.name || "").toUpperCase();
          const isAprende =
            prod.id === APRENDE_PRODUCT_ID || nameUpper.includes("APRENDE");
          const disableProduct =
            isTrainingService && !learnsToTrain && !isAprende;

          // Lógica para centro
          const centro = (prod.centro || '').toLowerCase();
          const needsCenterSelect = centro === 'todos' || centro === 'vip' || !centro;

          return (
            <div
              key={prod.id}
              onClick={() => {
                if (disableProduct) {
                  openTrainingInfo();
                  return;
                }
                if (isHired) {
                  // Si NO es suscripción, permitimos comprar otro bono igual
                  if (!isSubscription(prod)) {
                    const buyAnother = window.confirm(
                      'Ya tienes este bono activo. ¿Quieres comprar otro igual?'
                    );
                    if (!buyAnother) {
                      navigate(`/product/${prod.id}`);
                      return;
                    }
                    // Quiere comprar otro → seguimos al flujo de pago
                  } else {
                    // Suscripción: solo mostramos el detalle
                    navigate(`/product/${prod.id}`);
                    return;
                  }
                }
                // Flujo de selección de centro y pago (igual que antes)
                setSelectedProduct(prod);
                if (needsCenterSelect) {
                  setSelectedCenter(null);
                  setShowCenterModal(true);
                } else if (centro === 'guadarrama' || centro === 'sotillo') {
                  setSelectedCenter(centro);
                  setShowPaymentModal(true);
                } else {
                  setSelectedCenter(null);
                  setShowCenterModal(true);
                }
              }}
              style={{
                display: "flex",
                alignItems: "center",
                padding: "0.75rem",
                marginBottom: "0.5rem",
                border: "1px solid #eee",
                borderRadius: "12px",
                background: "#fff",
                cursor: disableProduct ? "not-allowed" : "pointer",
                boxShadow: "0 1px 3px rgba(0,0,0,0.05)",
                opacity: disableProduct ? 0.5 : 1,
              }}
            >
              {prod.image && (
                <img
                  src={`${API_BASE}/api/product_images/${prod.image}`}
                  alt={String(prod.name)}
                  style={{
                    width: "60px",
                    height: "60px",
                    objectFit: "contain",
                    marginRight: "12px",
                  }}
                />
              )}
              <div style={{ flex: 1 }}>
                <div style={{ fontWeight: 600, fontSize: "0.95rem" }}>
                  {nameUpper}
                </div>
                {isHired ? (
                  <div style={{ fontSize: "0.8rem", color: "#777" }}>
                    Ya contratado
                  </div>
                ) : (
                  prod.session != null && (
                    <div style={{ fontSize: "0.8rem", color: "#777" }}>
                      Sesiones: {prod.session}
                    </div>
                  )
                )}
              </div>
              <div style={{ textAlign: "right" }}>
                {hasDiscount ? (
                  <>
                    <div
                      style={{
                        textDecoration: "line-through",
                        color: "#aaa",
                        fontSize: "0.8rem",
                      }}
                    >
                      {(prod.price ?? 0).toFixed(0)}€
                    </div>
                    <div style={{ color: "#c62828", fontWeight: 600 }}>
                      {Math.round(discounted)}€
                    </div>
                  </>
                ) : (
                  <div style={{ color: "#c62828", fontWeight: 600 }}>
                    {Math.round(prod.price ?? 0)}€
                  </div>
                )}
              </div>
            </div>
          );
        })}

      {showCenterModal && selectedProduct && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            width: "100%",
            height: "100%",
            backgroundColor: "rgba(0,0,0,0.4)",
            display: "flex",
            flexDirection: "column",
            justifyContent: "flex-end",
            zIndex: 1000,
          }}
          onClick={() => {
            setShowCenterModal(false);
          }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              background: "#fff",
              borderTopLeftRadius: "16px",
              borderTopRightRadius: "16px",
              padding: "1.5rem",
            }}
          >
            <h4 style={{ marginTop: 0, marginBottom: "1rem" }}>
              Selecciona un centro
            </h4>
            <div style={{ display: "flex", flexDirection: "column", gap: "0.5rem" }}>
              {['Guadarrama', 'El Sotillo'].map((center) => (
                <button
                  key={center}
                  onClick={() => {
                    const mapping: Record<string, string> = {
                      'Guadarrama': 'guadarrama',
                      'El Sotillo': 'sotillo',
                    };
                    setSelectedCenter(mapping[center]);
                    setShowCenterModal(false);
                    setShowPaymentModal(true);
                  }}
                  style={{
                    padding: "0.75rem",
                    background: "#1976d2",
                    color: "#fff",
                    border: "none",
                    borderRadius: "8px",
                  }}
                >
                  {center}
                </button>
              ))}
              <button
                onClick={() => {
                  setShowCenterModal(false);
                  setSelectedProduct(null);
                }}
                style={{
                  padding: "0.75rem",
                  background: "transparent",
                  color: "#1976d2",
                  border: "none",
                }}
              >
                Cancelar
              </button>
            </div>
          </div>
        </div>
      )}

      {showPaymentModal && selectedProduct && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            width: "100%",
            height: "100%",
            backgroundColor: "rgba(0,0,0,0.4)",
            display: "flex",
            flexDirection: "column",
            justifyContent: "flex-end",
            zIndex: 1000,
          }}
          onClick={() => {
            setShowPaymentModal(false);
          }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              background: "#fff",
              borderTopLeftRadius: "16px",
              borderTopRightRadius: "16px",
              padding: "1.5rem",
            }}
          >
            <h4 style={{ marginTop: 0, marginBottom: "1rem" }}>
              Selecciona método de pago
            </h4>
            <div
              style={{ display: "flex", flexDirection: "column", gap: "0.5rem" }}
            >
              {/*
              <button
                onClick={() => {
                  if (selectedProduct) {
                    handlePayment(selectedProduct, 'gpay');
                  }
                }}
                style={{
                  padding: "0.75rem",
                  background: "#1976d2",
                  color: "#fff",
                  border: "none",
                  borderRadius: "8px",
                }}
              >
                G Pay
              </button>*/}

              <button
                onClick={() => {
                  if (selectedProduct) {
                    alert('🔴¡MUY IMPORTANTE!🔴 Para que tu suscripción mensual funcione sin interrupciones, debes marcar la casilla “Guardar detalles de tarjeta” en la siguiente pantalla\nTranquilo: nosotros no almacenamos tus datos, toda la información queda protegida y guardada de forma segura por CaixaBank.');
                    handlePayment(selectedProduct, 'card');
                  }
                }}
                style={{
                  padding: "0.75rem",
                  background: "#2196f3",
                  color: "#fff",
                  border: "none",
                  borderRadius: "8px",
                }}
              >
                Pagar con tarjeta 💳
              </button>
              <button
                onClick={() => {
                  if (selectedProduct) {
                    handlePayment(selectedProduct, 'ewallet');
                  }
                }}
                style={{
                  padding: "0.75rem",
                  background: "#81c784",
                  color: "#fff",
                  border: "none",
                  borderRadius: "8px",
                }}
              >
                Pagar con monedero virtual
              </button>
              <button
                onClick={() => {
                  setShowPaymentModal(false);
                  setSelectedProduct(null);
                }}
                style={{
                  padding: "0.75rem",
                  background: "transparent",
                  color: "#1976d2",
                  border: "none",
                }}
              >
                Cancelar
              </button>
            </div>
          </div>
        </div>
      )}

      {paymentHtml && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            width: "100%",
            height: "100%",
            zIndex: 2000,
            background: "#fff",
          }}
        >
          <PaymentWebView
            html={paymentHtml}
            onSuccess={async () => {
              setPaymentHtml(null);
              if (selectedProduct) {
                await hireProductAfterPayment(selectedProduct.id, 'card');
                setSelectedProduct(null);
              }
            }}
            onCancel={() => {
              setPaymentHtml(null);
              alert('Pago cancelado');
            }}
          />
        </div>
      )}
    </div>
  );
}
